/**
 * Pulse My Account iframe (`/pb-account`) — shared constants + pure helpers.
 */

export const PB_ACCOUNT_IFRAME_ID = 'iframePulseBlazor'

/** Default Pulse host when `NUXT_PUBLIC_PULSE_BLAZOR_BASE_URL` is unset. */
export const PULSE_BLAZOR_BASE_URL = 'https://pbtst.idtech.com'

export const PULSE_ALLOWED_ORIGINS = ['https://*.idtech.com'] as const

export const PULSE_TEST_EVENT_NAME = 'Pulse3Blazor_test'

export type PbAccountPublicConfig = {
  pbAccountIframeEnabled?: boolean
  pulseBlazorBaseUrl?: string
} | null | undefined

export function isPbAccountIframeEnabled (publicConfig: PbAccountPublicConfig): boolean {
  return publicConfig?.pbAccountIframeEnabled === true
}

/** Prefers runtime `pulseBlazorBaseUrl`; otherwise `PULSE_BLAZOR_BASE_URL`. */
export function resolvePulseBlazorBaseUrl (publicConfig: PbAccountPublicConfig): string {
  const fromConfig = publicConfig?.pulseBlazorBaseUrl?.trim()
  return fromConfig || PULSE_BLAZOR_BASE_URL
}

/**
 * Builds iframe URL: base + parent hash (without leading #).
 * Rejects crafted hashes that would change the iframe host (e.g. `#@evil.com`).
 */
export function buildPulseIframeUrlFromParentHash (
  baseUrl: string,
  parentHash: string
): string {
  const hashSegment = parentHash.startsWith('#') ? parentHash.slice(1) : parentHash
  const targetUrl = `${baseUrl}${hashSegment}`

  try {
    if (new URL(targetUrl).origin !== new URL(baseUrl).origin) {
      return baseUrl
    }
  } catch {
    return baseUrl
  }

  return targetUrl
}

/**
 * Parent hash segment (no leading `#`) from a Pulse `Url` message.
 * Requires matching origin (and path under the Pulse base) — not string prefix matching.
 */
export function pulseChildUrlToParentHashSegment (url: string, baseUrl: string): string {
  if (!url || !baseUrl) return ''

  try {
    const child = new URL(url)
    const base = new URL(baseUrl)

    if (child.origin !== base.origin) return ''

    const basePath = base.pathname === '/' ? '' : base.pathname.replace(/\/$/, '')
    const childPath = child.pathname

    if (basePath && childPath !== basePath && !childPath.startsWith(`${basePath}/`)) {
      return ''
    }

    let pathRemainder = basePath ? childPath.slice(basePath.length) : childPath
    if (!basePath && pathRemainder === '/') pathRemainder = ''

    const remainder = `${pathRemainder}${child.search}${child.hash}`
    if (!remainder) return ''

    if (
      !remainder.startsWith('/') &&
      !remainder.startsWith('?') &&
      !remainder.startsWith('#')
    ) {
      return `/${remainder}`
    }

    return remainder
  } catch {
    return ''
  }
}

export function findPulseIframeElement (
  doc: Pick<Document, 'getElementById' | 'getElementsByName'>,
  iframeId: string = PB_ACCOUNT_IFRAME_ID
): HTMLIFrameElement | null {
  const byId = doc.getElementById(iframeId)
  if (byId && byId.tagName === 'IFRAME') return byId as HTMLIFrameElement

  const byName = doc.getElementsByName(iframeId)?.[0]
  if (byName && byName.tagName === 'IFRAME') return byName as HTMLIFrameElement
  return null
}

/** Schedules work and returns a disposer that cancels it (for unmount-safe deferred sync). */
export function createCancellableTimeout (
  run: () => void,
  delayMs = 0,
  schedule: (handler: () => void, ms: number) => number = (handler, ms) =>
    globalThis.setTimeout(handler, ms) as unknown as number,
  clear: (id: number) => void = (id) => globalThis.clearTimeout(id)
): () => void {
  let cancelled = false
  const timeoutId = schedule(() => {
    if (!cancelled) run()
  }, delayMs)

  return () => {
    cancelled = true
    clear(timeoutId)
  }
}
